import React, { useState, useRef, useEffect } from 'react';
import { Sparkles, X, Send, Gem, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { conciergeConfig } from '../mock';
import { API } from '../lib/api';
import { toast } from 'sonner';

const DEMO_SCRIPT = [
  'Which offer should I start with?',
  'Is the $150 call really worth it?',
  "What if it doesn't work for me?",
];

function sessionId() {
  return 'sess_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export default function AIConcierge({ autoDemo = false, handle = 'aria.mercer' }) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([{ role: 'ai', text: conciergeConfig.greeting }]);
  const [typing, setTyping] = useState(false);
  const [leadDone, setLeadDone] = useState(false);
  const [leadDismissed, setLeadDismissed] = useState(false);
  const [leadEmail, setLeadEmail] = useState('');
  const scrollRef = useRef(null);
  const sidRef = useRef(sessionId());
  const timers = useRef([]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, typing, open]);

  const askAI = async (text) => {
    setTyping(true);
    try {
      const { data } = await API.post('/chat', { handle, session_id: sidRef.current, message: text });
      setMessages((m) => [...m, { role: 'ai', text: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: 'ai', text: "I'm having a moment — but I'd genuinely recommend the 1-on-1 Growth Audit to start. Want the link?" }]);
    } finally {
      setTyping(false);
    }
  };

  const send = (text) => {
    const t = (text ?? input).trim();
    if (!t) return;
    setMessages((m) => [...m, { role: 'user', text: t }]);
    setInput('');
    askAI(t);
  };

  useEffect(() => {
    if (!autoDemo) return;
    let cancelled = false;
    const run = async () => {
      await new Promise((r) => timers.current.push(setTimeout(r, 1200)));
      if (cancelled) return;
      setOpen(true);
      for (const line of DEMO_SCRIPT) {
        await new Promise((r) => timers.current.push(setTimeout(r, 1600)));
        if (cancelled) return;
        setMessages((m) => [...m, { role: 'user', text: line }]);
        await askAI(line);
      }
    };
    run();
    return () => { cancelled = true; timers.current.forEach(clearTimeout); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoDemo]);

  const submitLead = async (e) => {
    e.preventDefault();
    if (!leadEmail) return;
    try {
      await API.post('/leads', { handle, email: leadEmail, source: 'concierge', context: 'AI chat' });
      setLeadDone(true);
      setMessages((m) => [...m, { role: 'ai', text: `Perfect — I'll send a personalized plan to ${leadEmail}. Want me to link you to the best offer? 💎` }]);
      toast.success('Thanks! Your plan is on the way.');
    } catch { toast.error('Could not save email'); }
  };

  const showLead = !autoDemo && !leadDone && !leadDismissed && messages.filter((m) => m.role === 'user').length >= 1;

  return (
    <>
      <button
        onClick={() => setOpen((o) => !o)}
        className={`animate-pulse-ring z-50 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground transition-luxe hover:scale-110 ${autoDemo ? 'absolute bottom-5 right-5' : 'fixed bottom-5 right-5'}`}
        aria-label="AI Concierge"
      >
        {open ? <X className="h-6 w-6" /> : <Sparkles className="h-6 w-6" />}
      </button>

      {open && (
        <div className={`glass-strong animate-fade-up z-50 flex h-[30rem] w-[calc(100%-2.5rem)] max-w-sm flex-col overflow-hidden rounded-3xl shadow-2xl ${autoDemo ? 'absolute bottom-24 right-5' : 'fixed bottom-24 right-5'}`}>
          <div className="flex items-center gap-3 border-b border-white/10 p-4">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/15"><Sparkles className="h-4 w-4 text-primary" /></div>
            <div className="flex-1">
              <p className="font-display text-sm font-semibold text-white">AI Sales Concierge</p>
              <p className="flex items-center gap-1 text-[11px] text-primary"><span className="h-1.5 w-1.5 rounded-full bg-primary" /> Online now</p>
            </div>
            <span className="flex items-center gap-1 rounded-full bg-primary/15 px-2 py-1 text-[10px] font-semibold text-primary"><Gem className="h-3 w-3" /> PRO</span>
          </div>

          <div ref={scrollRef} className="no-scrollbar flex-1 space-y-3 overflow-y-auto p-4">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${m.role === 'user' ? 'bg-primary text-primary-foreground rounded-br-md' : 'bg-white/[0.06] text-white/90 rounded-bl-md'}`}>{m.text}</div>
              </div>
            ))}
            {typing && (
              <div className="flex justify-start">
                <div className="flex gap-1 rounded-2xl rounded-bl-md bg-white/[0.06] px-4 py-3">
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-white/60" style={{ animationDelay: '0ms' }} />
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-white/60" style={{ animationDelay: '150ms' }} />
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-white/60" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            )}
            {showLead && (
              <form onSubmit={submitLead} className="animate-fade-up rounded-2xl border border-primary/20 bg-primary/[0.06] p-3">
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-xs text-white/85">Want a personalized plan? Drop your email 👇</p>
                  <button type="button" onClick={() => setLeadDismissed(true)} className="text-white/40 hover:text-white"><X className="h-3.5 w-3.5" /></button>
                </div>
                <div className="flex gap-2">
                  <Input value={leadEmail} onChange={(e) => setLeadEmail(e.target.value)} placeholder="you@email.com" className="h-9 rounded-lg border-white/10 bg-white/[0.05] text-sm focus-visible:ring-primary" />
                  <Button type="submit" size="icon" className="h-9 w-9 shrink-0 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90"><ArrowRight className="h-4 w-4" /></Button>
                </div>
              </form>
            )}
          </div>

          {messages.length <= 1 && !autoDemo && (
            <div className="no-scrollbar flex gap-2 overflow-x-auto px-4 pb-2">
              {conciergeConfig.quickReplies.map((q) => (
                <button key={q} onClick={() => send(q)} className="transition-luxe shrink-0 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5 text-xs text-white/80 hover:border-primary/40 hover:text-white">{q}</button>
              ))}
            </div>
          )}

          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2 border-t border-white/10 p-3">
            <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask anything..." className="rounded-full border-white/10 bg-white/[0.04] focus-visible:ring-primary" />
            <Button type="submit" size="icon" className="h-10 w-10 shrink-0 rounded-full bg-primary text-primary-foreground hover:bg-primary/90"><Send className="h-4 w-4" /></Button>
          </form>
        </div>
      )}
    </>
  );
}
